import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { LINKS, openExternal, waIntent, WHATSAPP_DIGITS } from "@/constants/links";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const NUMBERS = Array.from({ length: 100 }, (_, i) => String(i).padStart(2, "0"));

// Nube flotante de la rifa: tablero 00-99. El cliente toca un número libre,
// pone su nombre, paga con el link SumUp del salón y aparece en la listina.
export default function RaffleCloud() {
  const [raffle, setRaffle] = useState(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [nome, setNome] = useState("");
  const [telefono, setTelefono] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [justPicked, setJustPicked] = useState(null);
  const [myNumbers, setMyNumbers] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("raffle_my_numbers") || "[]");
    } catch {
      return [];
    }
  });

  const load = () =>
    fetch(`${BACKEND}/api/raffle`)
      .then((r) => r.json())
      .then((d) => setRaffle(d))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  if (!raffle || !raffle.active) return null; // rifa desactivada -> la nube se esconde

  const taken = new Set((raffle.entries || []).map((e) => e.number));
  const prizes = raffle.prizes || [];

  const pick = async () => {
    if (!selected || nome.trim().length < 2 || telefono.replace(/\D/g, "").length < 8) return;
    setSaving(true);
    setMsg("");
    const res = await fetch(`${BACKEND}/api/raffle/pick`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ number: selected, nome: nome.trim(), telefono: telefono.trim() }),
    });
    setSaving(false);
    if (res.ok) {
      const mine = [...myNumbers, selected];
      setMyNumbers(mine);
      localStorage.setItem("raffle_my_numbers", JSON.stringify(mine));
      setJustPicked(selected);
      setSelected(null);
      setMsg("ok");
      await load();
    } else {
      const err = await res.json().catch(() => ({}));
      setMsg(err.detail || "Errore: riprova");
      await load();
    }
  };

  const waText = justPicked
    ? `Ciao! Sono ${nome.trim()}, ho preso il numero ${justPicked} della tombola 🎟️`
    : "Ciao! Ho comprato il mio numero per la tombola 🎟️";
  const payUrl = raffle.pay_link || LINKS.pay;

  return (
    <>
      <motion.button
        data-testid="raffle-cloud-btn"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 16, delay: 3 }}
        whileTap={{ scale: 0.92 }}
        onClick={() => setOpen(true)}
        className="fixed bottom-40 right-4 z-[62] flex items-center gap-2 rounded-full border border-emerald-400/50 bg-zinc-900/95 py-2.5 pl-3 pr-4 shadow-[0_4px_24px_rgba(52,211,153,0.35)] backdrop-blur"
      >
        <span className="text-lg leading-none">🎟️</span>
        <span className="text-[11px] font-extrabold uppercase tracking-widest text-emerald-300">
          Tombola
        </span>
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[65] bg-black/60"
            />
            <motion.div
              data-testid="raffle-cloud-panel"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className="fixed inset-x-0 bottom-0 z-[70] mx-auto max-h-[88vh] w-full max-w-md overflow-y-auto rounded-t-3xl border-t border-emerald-400/30 bg-zinc-900 p-5 pb-10 shadow-[0_-8px_40px_rgba(0,0,0,0.6)]"
            >
              <div className="mb-1 flex items-center justify-between">
                <p className="text-sm font-extrabold uppercase tracking-widest text-emerald-300">
                  🎉 {raffle.title || "Tombola del salone"}
                </p>
                <button
                  data-testid="raffle-cloud-close"
                  onClick={() => setOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-zinc-400 transition-colors hover:text-white"
                >
                  <X size={15} />
                </button>
              </div>
              <p className="mb-3 text-xs leading-relaxed text-zinc-500">
                Scegli il tuo numero fortunato{raffle.price ? ` a ${raffle.price}` : ""} e partecipa
                al sorteggio. 🍀
              </p>
              {raffle.note && (
                <p
                  data-testid="raffle-note"
                  className="mb-4 rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-3 text-xs leading-relaxed text-zinc-300"
                >
                  {raffle.note}
                </p>
              )}

              {prizes.length > 0 && (
                <div
                  data-testid="raffle-prizes"
                  className="mb-4 flex snap-x gap-2 overflow-x-auto pb-1"
                >
                  {prizes.map((p, i) => (
                    <div
                      key={p.id}
                      data-testid={`raffle-prize-${i}`}
                      className="flex w-36 shrink-0 snap-start flex-col gap-1.5 rounded-2xl border border-white/10 bg-zinc-950/70 p-2"
                    >
                      {p.storage_path ? (
                        <img
                          src={`${BACKEND}/api/media/file/${p.storage_path}`}
                          alt={p.name || "Premio"}
                          className="h-20 w-full rounded-xl object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <span className="flex h-20 w-full items-center justify-center rounded-xl bg-emerald-400/10 text-3xl">
                          🏆
                        </span>
                      )}
                      <span className="truncate text-[11px] font-bold text-white">
                        🏆 {p.name || `Premio ${i + 1}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-zinc-500">
                Scegli il tuo numero
              </p>
              <div data-testid="raffle-grid" className="mb-3 grid grid-cols-10 gap-1.5">
                {NUMBERS.map((n) => {
                  const isTaken = taken.has(n);
                  const isMine = myNumbers.includes(n);
                  const isSel = selected === n;
                  return (
                    <button
                      key={n}
                      data-testid={`raffle-num-${n}`}
                      disabled={isTaken}
                      onClick={() => {
                        setSelected(isSel ? null : n);
                        setMsg("");
                      }}
                      className={`flex h-9 items-center justify-center rounded-lg text-xs font-extrabold transition-all duration-150 ${
                        isTaken
                          ? isMine
                            ? "border border-amber-400/70 bg-amber-400/20 text-amber-300"
                            : "bg-emerald-400/15 text-emerald-200/60 line-through"
                          : isSel
                            ? "scale-110 border border-emerald-300 bg-emerald-400 text-zinc-950"
                            : "border border-white/15 bg-zinc-950/60 text-zinc-300 hover:border-emerald-400/60 active:scale-95"
                      }`}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
              <p className="mb-3 text-[10px] text-zinc-600">
                <span className="text-emerald-300">Verde</span> = già preso ·{" "}
                <span className="text-amber-300">Giallo</span> = il tuo
              </p>

              <AnimatePresence>
                {selected && (
                  <motion.div
                    data-testid="raffle-pick-form"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-3 overflow-hidden rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-3"
                  >
                    <p className="mb-2 text-xs font-bold text-emerald-300">
                      Numero {selected} 🍀 — scrivi i tuoi dati per comprarlo
                    </p>
                    <input
                      data-testid="raffle-nome"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      placeholder="Il tuo nome"
                      className="mb-2 h-10 w-full rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                    />
                    <input
                      data-testid="raffle-telefono"
                      value={telefono}
                      onChange={(e) => setTelefono(e.target.value)}
                      placeholder="Il tuo WhatsApp (obbligatorio)"
                      className="mb-2 h-10 w-full rounded-xl border border-white/10 bg-zinc-950 px-3 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-emerald-400/50"
                    />
                    <p className="mb-2 text-[10px] text-zinc-600">
                      Serve per contattarti se vinci il sorteggio 🏆
                    </p>
                    <button
                      data-testid="raffle-pick-confirm"
                      onClick={pick}
                      disabled={
                        saving || nome.trim().length < 2 || telefono.replace(/\D/g, "").length < 8
                      }
                      className="w-full rounded-xl bg-emerald-400 py-2.5 text-[12px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-emerald-300 disabled:opacity-40"
                    >
                      {saving ? "..." : `🎟️ Compra il ${selected}`}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {msg && msg !== "ok" && (
                <p data-testid="raffle-msg" className="mb-3 text-[11px] font-semibold text-red-400">
                  {msg}
                </p>
              )}

              <AnimatePresence>
                {justPicked && (
                  <motion.div
                    data-testid="raffle-pay-box"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mb-4 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-3"
                  >
                    <p className="mb-1 text-xs font-extrabold text-amber-300">
                      🎟️ Numero {justPicked} quasi tuo!
                    </p>
                    <p className="mb-3 text-[11px] leading-relaxed text-zinc-400">
                      Per completare l&apos;acquisto: 1) paga
                      {raffle.price ? ` ${raffle.price}` : ""} con il link qui sotto — 2) avvisa su
                      WhatsApp. Il barbiere conferma il pagamento e il numero è ufficialmente tuo. 💚
                    </p>
                    <button
                      data-testid="raffle-buy-btn"
                      onClick={openExternal(payUrl)}
                      className="mb-2 w-full rounded-xl bg-emerald-400 py-3 text-[12px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-emerald-300 active:scale-[0.98]"
                    >
                      🎟️ Paga ora{raffle.price ? ` — ${raffle.price}` : ""}
                    </button>
                    <button
                      data-testid="raffle-wa-btn"
                      onClick={openExternal(LINKS.whatsapp, waIntent(WHATSAPP_DIGITS, waText))}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 py-2.5 text-[11px] font-bold text-zinc-300 transition-colors hover:border-emerald-400/40 hover:text-emerald-300"
                    >
                      <FaWhatsapp size={13} className="text-emerald-400" />
                      Conferma su WhatsApp
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              <p
                data-testid="raffle-remaining"
                className="rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-3 text-center text-xs font-bold text-emerald-300"
              >
                🔥 Numeri rimasti: {100 - (raffle.entries || []).length} su 100 — affrettati!
              </p>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
